import { theme } from '@contrast-security-inc/design-system-foundations'
import { css, cx } from '@emotion/css'
import React from 'react'
import { FormattedMessage } from 'react-intl'
import { AgentFeature } from '../agent-features'
import { AgentLanguage } from '../agent-language/agent-language'
import { ApplicationAttackStatus } from '../application-attack-status'
import { ApplicationGrade } from '../application-grade'
import {
  CompliancePolicy,
  VulnerabilitySeverityBreakdown
} from '../applications-grid/shared'
import { ViolationIndicator } from '../applications-grid/violation-indicator'
import { AttackStatus } from '../attack-status'
import { BasicTooltipContent } from '../../legacy-design-system/basic-tooltip-content'
import { ContrastFormattedMessage } from '../contrast-formatted-message'
import { CsHelpCircledIcon } from '../../legacy-design-system/icons'
import { Role } from '@contrast-security-inc/base-types'
import { serverEnvI18n, ServerEnvironment } from '../server-environment'
import { metadataCss, panelTitleCss } from '../../legacy-design-system/text'
import { OpenVulnerabilitiesSeverityThermometer } from '../thermometer'
import { WithTooltip } from '../../design-system'
import { ApplicationTechnologyItem } from './application-technology-item'
import { FlowMapColumnTitle } from './column-title'
import {
  ApplicationLayerType,
  architectureColumnMinHeight,
  diagramHeight
} from './index'

interface BaseLibrary {
  readonly name: string
  readonly hash: string
}

interface Library extends BaseLibrary {
  readonly vulnerable?: boolean
}

interface ApplicationTechnology {
  readonly name: string
  readonly libraries: Library[]
}

type ApplicationTechnologiesBreakdown = {
  readonly [key in ApplicationLayerType]: ApplicationTechnology[]
}

interface ApplicationArchitectureCardProps {
  readonly organizationID: string
  readonly applicationID: string
  readonly online: boolean
  readonly assess: boolean
  readonly hasAProtectedServer: boolean
  readonly isFreemiumOrganization: boolean
  readonly features?: AgentFeature[]
  readonly language: AgentLanguage
  readonly violatedCompliancePolicies: CompliancePolicy[]
  readonly organizationRole: Role
  readonly vulnerabilityBreakdown: VulnerabilitySeverityBreakdown
  readonly attackStatus: AttackStatus

  readonly serverEnvironments: ServerEnvironment[]
  readonly technologiesBreakdown: ApplicationTechnologiesBreakdown
}

const smallTextSize = 11
export const containerWidth = 700
export const negativeMargin = 15

const smallMetadataCss = cx(
  metadataCss,
  css`
    font-size: ${smallTextSize}px;
  `
)

export function ApplicationArchitectureCard({
  applicationID,
  organizationID,
  features,
  language,
  online,
  isFreemiumOrganization,
  violatedCompliancePolicies,
  organizationRole,
  vulnerabilityBreakdown,
  assess,
  attackStatus,
  hasAProtectedServer,
  serverEnvironments,
  technologiesBreakdown
}: ApplicationArchitectureCardProps) {
  const appTechnologiesConfig: Array<{
    readonly technologies: ApplicationTechnology[]
    readonly i18nLayerName: string
    readonly i18nTooltipTitle: string
    readonly i18nTooltipDescription: string
  }> = [
    {
      technologies: technologiesBreakdown.view,
      i18nLayerName: 'title_applicationFlowMap_appArchitectureLayer_view',
      i18nTooltipTitle:
        'title_applicationFlowMap_appArchitectureLayer_viewLayer',
      i18nTooltipDescription:
        'detail_applicationFlowMap_appArchitectureLayer_viewLayer'
    },
    {
      technologies: technologiesBreakdown.presentation,
      i18nLayerName:
        'title_applicationFlowMap_appArchitectureLayer_presentation',
      i18nTooltipTitle:
        'title_applicationFlowMap_appArchitectureLayer_presentationLayer',
      i18nTooltipDescription:
        'detail_applicationFlowMap_appArchitectureLayer_presentationLayer'
    },
    {
      technologies: technologiesBreakdown.service,
      i18nLayerName: 'title_applicationFlowMap_appArchitectureLayer_service',
      i18nTooltipTitle:
        'title_applicationFlowMap_appArchitectureLayer_serviceLayer',
      i18nTooltipDescription:
        'detail_applicationFlowMap_appArchitectureLayer_serviceLayer'
    }
  ]

  const IconSeparator = () => (
    <div
      className={css`
        width: 4px;
      `}
    />
  )

  const hasViolation =
    violatedCompliancePolicies.length > 0 && !isFreemiumOrganization

  return (
    <div
      className={css`
        width: ${containerWidth}px;
        min-height: ${architectureColumnMinHeight}px;
        background-color: ${theme.color.white};
        border-color: ${theme.color.neutral50};
        border-width: 1.2px;
        border-radius: 10px;
        border-style: solid;
        padding: 10px 15px 15px 20px;
      `}
    >
      <div
        className={css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
        `}
      >
        <FlowMapColumnTitle
          titleI18n={'title_applicationArchitecture_appArchitecture'}
          position="flex-start"
        />

        {Array.isArray(serverEnvironments) && serverEnvironments.length > 0 && (
          <div
            className={css`
              display: flex;
              flex-direction: row;
              height: fit-content;
              margin-top: 3px;
            `}
          >
            {serverEnvironments.map((env, index) => (
              <FormattedMessage key={index} id={serverEnvI18n[env]}>
                {(text) => {
                  const isLastChild = serverEnvironments.length - 1 !== index

                  return (
                    <span
                      className={cx(
                        metadataCss,
                        css`
                          display: flex;
                          margin-right: 3px;
                        `
                      )}
                    >
                      {text} {isLastChild && '|'}
                    </span>
                  )
                }}
              </FormattedMessage>
            ))}

            <WithTooltip
              renderTooltipContent={() => (
                <BasicTooltipContent
                  i18nTitle="title_environment_server"
                  i18nDescription="detail_applicationFlowMap_applicationEnvironments"
                />
              )}
              children={() => (
                <CsHelpCircledIcon color={theme.color.neutral60} size={18} />
              )}
              positionFixed={true}
              tooltipMaxWidth="200px"
            />
          </div>
        )}
      </div>

      <div
        className={css`
          display: flex;
          flex: 1;
          flex-direction: row;
          align-items: center;
          margin-bottom: 22px;
        `}
      >
        <div
          className={css`
            align-self: flex-start;
            padding-top: 2px;
          `}
        >
          <ApplicationGrade
            id={applicationID}
            organizationID={organizationID}
            language={language}
            features={features}
          />
        </div>

        <IconSeparator />
        <IconSeparator />

        <div
          className={css`
            display: flex;
            flex: 1;
            flex-direction: column;
            justify-content: center;
          `}
        >
          <div
            className={cx(
              smallMetadataCss,
              css`
                margin-bottom: -4px;
              `
            )}
          >
            {online ? (
              <React.Fragment>
                {!assess && !hasAProtectedServer && (
                  <FormattedMessage id={'title_featureTypeInventory'} />
                )}
                {assess && <FormattedMessage id={'title_featureTypeAssess'} />}
                {assess && hasAProtectedServer && <span>/</span>}
                {hasAProtectedServer && (
                  <FormattedMessage id={'title_featureTypeProtect'} />
                )}
              </React.Fragment>
            ) : (
              <FormattedMessage id={'title_featureTypeContrastOff'} />
            )}
          </div>

          <div
            className={css`
              display: flex;
              flex-direction: row;
              align-items: center;
            `}
          >
            <OpenVulnerabilitiesSeverityThermometer
              enableTooltips
              breakdown={vulnerabilityBreakdown}
            />

            <IconSeparator />
            <IconSeparator />

            {vulnerabilityBreakdown.open}

            {hasViolation && (
              <React.Fragment>
                <IconSeparator />
                <ViolationIndicator
                  organizationID={organizationID}
                  organizationRole={organizationRole}
                  violatedCompliancePolicies={violatedCompliancePolicies}
                />
              </React.Fragment>
            )}
          </div>
        </div>

        <IconSeparator />
        <div
          className={css`
            display: flex;
            flex: 1;
            justify-content: flex-end;
          `}
        >
          <ApplicationAttackStatus
            small
            hasAProtectedServer={hasAProtectedServer}
            attackStatus={attackStatus}
          />
        </div>
      </div>

      <div
        className={css`
          display: flex;
          flex: 1;
          flex-direction: row;

          max-height: ${diagramHeight - 60}px;
          overflow-y: auto;
        `}
      >
        {appTechnologiesConfig.map(
          (
            {
              i18nLayerName,
              technologies,
              i18nTooltipTitle,
              i18nTooltipDescription
            },
            index
          ) => (
            <ApplicationArchitectureTechnologies
              key={index}
              language={language}
              organizationID={organizationID}
              technologies={technologies}
              i18nLayerName={i18nLayerName}
              i18nTooltipTitle={i18nTooltipTitle}
              i18nTooltipDescription={i18nTooltipDescription}
              applicationID={applicationID}
            />
          )
        )}
      </div>
    </div>
  )
}

function ApplicationArchitectureTechnologies({
  language,
  organizationID,
  technologies,
  i18nLayerName,
  i18nTooltipTitle,
  i18nTooltipDescription,
  applicationID
}: {
  readonly language: AgentLanguage
  readonly organizationID: string
  readonly applicationID: string
  readonly technologies: ApplicationTechnology[]
  readonly i18nLayerName: string
  readonly i18nTooltipTitle: string
  readonly i18nTooltipDescription: string
}) {
  return (
    <div
      className={css`
        display: flex;
        flex: 1;
        flex-direction: column;
        margin-right: 12px;
        padding-right: 12px;
        border-right: 1px solid ${theme.color.neutral30};

        :last-child {
          margin-right: 0px;
          padding-right: 0px;
          border-right: 0px solid ${theme.color.transparent};
        }
      `}
    >
      <div
        className={css`
          display: flex;
          flex-direction: row;
          margin-bottom: 6px;
        `}
      >
        <ContrastFormattedMessage id={i18nLayerName}>
          {(title) => (
            <div
              className={cx(
                panelTitleCss,
                css`
                  padding-top: 1px;
                  padding-left: 2px;
                  margin-right: 4px;
                `
              )}
              children={title.toUpperCase()}
            />
          )}
        </ContrastFormattedMessage>

        <WithTooltip
          renderTooltipContent={() => (
            <BasicTooltipContent
              i18nTitle={i18nTooltipTitle}
              i18nDescription={i18nTooltipDescription}
            />
          )}
          children={() => (
            <CsHelpCircledIcon color={theme.color.neutral60} size={18} />
          )}
        />
      </div>
      {Array.isArray(technologies) && technologies.length > 0 && (
        <div>
          {technologies.map(({ name, libraries }, index) => (
            <ApplicationTechnologyItem
              key={index}
              organizationID={organizationID}
              technologyName={name}
              libraries={libraries}
              language={language}
              applicationID={applicationID}
            />
          ))}
        </div>
      )}
    </div>
  )
}
